import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  Renderer2,
} from '@angular/core';

/**
 * Adds spreadsheet-style column resizing and reordering to a `mat-table`.
 *
 * Applied as `<table mat-table appGridTable gridTableKey="gift-certificates">`.
 * Widths and column order persist per table key in localStorage, so a user's
 * layout survives navigation and reloads.
 *
 * Built on plain DOM listeners rather than a grid library: Handsontable is in
 * package.json but is commercially licensed and its module is not wired up.
 */
@Directive({
  selector: 'table[appGridTable]',
})
export class GridTableDirective implements AfterViewInit, OnDestroy {
  /** Unique key for persisting this table's layout. Omit to disable persistence. */
  @Input() gridTableKey?: string;

  /** Minimum column width in pixels. */
  @Input() gridMinWidth = 64;

  /** Adds the Airtable-style leading row-number gutter. */
  @Input() gridRowNumbers = true;

  private _cleanups: Array<() => void> = [];
  private _resizing: {
    th: HTMLElement;
    startX: number;
    startWidth: number;
  } | null = null;

  /** Columns the user has explicitly resized (or restored from storage). */
  private _resizedIds = new Set<string>();

  constructor(
    private _el: ElementRef<HTMLTableElement>,
    private _renderer: Renderer2,
  ) {}

  ngAfterViewInit(): void {
    // Header cells only exist after the table's first render pass.
    this._defer(() => this._setup());
  }

  ngOnDestroy(): void {
    this._cleanups.forEach(fn => fn());
    this._cleanups = [];
  }

  private _defer(fn: () => void): void {
    setTimeout(fn);
  }

  private _setup(): void {
    const headers = this._headerCells();
    if (!headers.length) {
      return;
    }

    this._renderer.addClass(this._el.nativeElement, 'grid-table');

    // Auto layout by default so columns size to their content. Fixed layout is
    // only switched on once explicit widths exist, since it would otherwise
    // divide the table evenly and blow out narrow columns like the expander.
    this._restoreWidths(headers);

    headers.forEach(th => {
      this._renderer.addClass(th, 'grid-col');
      this._addResizeHandle(th);
    });

    if (this.gridRowNumbers) {
      this._renderRowNumbers();
      this._observeRows();
    }
  }

  /**
   * Numbers the visible data rows in a leading gutter, the way a spreadsheet
   * does. Written as a ::before on the first cell so no column definition is
   * required and expansion/detail rows are skipped.
   */
  private _renderRowNumbers(): void {
    this._renderer.addClass(this._el.nativeElement, 'grid-table-numbered');
    const rows = Array.from(
      this._el.nativeElement.querySelectorAll<HTMLTableRowElement>('tbody tr.mat-row'),
    );

    let n = 0;
    rows.forEach(tr => {
      // Detail/expansion rows carry a single full-width cell; don't number them.
      if (tr.classList.contains('detail-row') || tr.cells.length <= 1) {
        this._renderer.removeAttribute(tr, 'data-row-number');
        return;
      }
      n += 1;
      // attr() can only read the element it is applied to, so the number lives
      // on the first cell, which is where the ::before gutter is drawn.
      this._renderer.setAttribute(tr, 'data-row-number', String(n));
      const firstCell = tr.cells[0];
      if (firstCell) {
        this._renderer.setAttribute(firstCell, 'data-row-number', String(n));
      }
    });
  }

  /** Re-number after paging, sorting or filtering replaces the rows. */
  private _observeRows(): void {
    const body = this._el.nativeElement.querySelector('tbody');
    if (!body || typeof MutationObserver === 'undefined') {
      return;
    }
    const observer = new MutationObserver(() => this._renderRowNumbers());
    observer.observe(body, { childList: true, subtree: false });
    this._cleanups.push(() => observer.disconnect());
  }

  private _headerCells(): HTMLElement[] {
    return Array.from(
      this._el.nativeElement.querySelectorAll<HTMLElement>('thead th'),
    );
  }

  /** Column identity comes from Material's generated `mat-column-<name>` class. */
  private _columnId(th: HTMLElement): string | null {
    const match = Array.from(th.classList).find(c => c.startsWith('mat-column-'));
    return match ? match.replace('mat-column-', '') : null;
  }

  private _addResizeHandle(th: HTMLElement): void {
    const handle = this._renderer.createElement('span');
    this._renderer.addClass(handle, 'grid-col-resize');
    this._renderer.setAttribute(handle, 'aria-hidden', 'true');
    this._renderer.appendChild(th, handle);

    const onDown = (event: MouseEvent) => {
      event.preventDefault();
      // Stop the sort header from firing on a resize drag.
      event.stopPropagation();
      this._resizing = {
        th,
        startX: event.pageX,
        startWidth: th.getBoundingClientRect().width,
      };
      this._renderer.addClass(document.body, 'grid-col-resizing');
    };

    this._cleanups.push(
      this._renderer.listen(handle, 'mousedown', onDown),
      // A click on the handle must never reach the sort header.
      this._renderer.listen(handle, 'click', (e: Event) => e.stopPropagation()),
    );

    this._bindWindowHandlers();
  }

  private _windowBound = false;

  private _bindWindowHandlers(): void {
    if (this._windowBound) {
      return;
    }
    this._windowBound = true;

    const onMove = (event: MouseEvent) => {
      if (!this._resizing) {
        return;
      }
      const delta = event.pageX - this._resizing.startX;
      const width = Math.max(this.gridMinWidth, this._resizing.startWidth + delta);
      const id = this._columnId(this._resizing.th);
      if (id) {
        this._resizedIds.add(id);
      }
      this._applyWidth(this._resizing.th, width);
    };

    const onUp = () => {
      if (!this._resizing) {
        return;
      }
      this._resizing = null;
      this._renderer.removeClass(document.body, 'grid-col-resizing');
      this._persistWidths();
    };

    this._cleanups.push(
      this._renderer.listen('window', 'mousemove', onMove),
      this._renderer.listen('window', 'mouseup', onUp),
    );
  }

  /** Switches the table to fixed layout so explicit widths are authoritative. */
  private _enableFixedLayout(): void {
    this._renderer.setStyle(this._el.nativeElement, 'table-layout', 'fixed');
  }

  /** Width must be set on the header AND the body cells under fixed layout. */
  private _applyWidth(th: HTMLElement, width: number): void {
    this._enableFixedLayout();
    const id = this._columnId(th);
    this._renderer.setStyle(th, 'width', `${width}px`);
    if (!id) {
      return;
    }
    const cells = this._el.nativeElement.querySelectorAll<HTMLElement>(
      `td.mat-column-${id}`,
    );
    cells.forEach(cell => this._renderer.setStyle(cell, 'width', `${width}px`));
  }

  private get _storageKey(): string | null {
    return this.gridTableKey ? `grid-table:${this.gridTableKey}:widths` : null;
  }

  private _persistWidths(): void {
    const key = this._storageKey;
    if (!key) {
      return;
    }
    // Merge into any existing record so untouched columns stay auto-sized.
    let widths: Record<string, number> = {};
    try {
      widths = JSON.parse(localStorage.getItem(key) || '{}') || {};
    } catch {
      widths = {};
    }
    const resized = this._resizedIds;
    this._headerCells().forEach(th => {
      const id = this._columnId(th);
      if (id && resized.has(id)) {
        widths[id] = Math.round(th.getBoundingClientRect().width);
      }
    });
    try {
      localStorage.setItem(key, JSON.stringify(widths));
    } catch {
      // Private browsing or blocked storage: layout simply won't persist.
    }
  }

  private _restoreWidths(headers: HTMLElement[]): void {
    const key = this._storageKey;
    if (!key) {
      return;
    }
    let widths: Record<string, number> = {};
    try {
      widths = JSON.parse(localStorage.getItem(key) || '{}') || {};
    } catch {
      return;
    }
    headers.forEach(th => {
      const id = this._columnId(th);
      if (id && widths[id]) {
        this._resizedIds.add(id);
        this._applyWidth(th, widths[id]);
      }
    });
  }
}
